import React from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Users, ClipboardCheck, BarChart3, Settings, Upload, LogOut,
  History, TreePine, ShieldCheck, UserCircle, ListOrdered, Monitor, HelpCircle,
  PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import Logo from "@/components/Logo";
import { hasClientPrivacy } from "@/lib/client-privacy";
import { canUseStaffPage } from "@/lib/page-permissions";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

const NAV = [
  { to: "/staff/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", slug: "dashboard" },
  { to: "/staff/clients", label: "Clients", icon: Users, testid: "nav-clients", slug: "clients" },
  { to: "/staff/queue", label: "Saturday Queue", icon: ListOrdered, testid: "nav-queue", viewerHidden: true, slug: "queue" },
  { to: "/staff/pending", label: "Pending Approvals", icon: ClipboardCheck, testid: "nav-pending", slug: "pending" },
  { to: "/staff/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", slug: "reports" },
  { to: "/staff/sharing-tree", label: "Sharing Tree", icon: TreePine, testid: "nav-sharing-tree", slug: "sharing-tree", viewerHidden: true },
  { to: "/staff/import", label: "Import CSV", icon: Upload, testid: "nav-import", slug: "import", viewerHidden: true },
  { to: "/staff/audit", label: "Audit Log", icon: History, testid: "nav-audit", slug: "audit", viewerHidden: true },
  { to: "/staff/settings", label: "Settings", icon: Settings, testid: "nav-settings", slug: "settings", viewerHidden: true },
  { to: "/staff/users", label: "Staff Logins", icon: ShieldCheck, testid: "nav-users", adminOnly: true },
  { to: "/staff/kiosk", label: "Kiosk Display", icon: Monitor, testid: "nav-kiosk", viewerHidden: true, newTab: true, slug: "kiosk" },
];

function isPageAllowed(user, slug) {
  return !slug || canUseStaffPage(user, slug);
}

// Portal-based tooltips remain outside the sidebar's scrolling/clipping boundary.
function SidebarTip({ label, enabled, children }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      {enabled && <TooltipContent side="right" sideOffset={12} collisionPadding={8}>{label}</TooltipContent>}
    </Tooltip>
  );
}

// Radix Slot merges className as text. Keep NavLink's function below that boundary.
const SidebarRouteLink = React.forwardRef(function SidebarRouteLink({ className, ...props }, ref) {
  return <NavLink {...props} ref={ref}
    className={({ isActive }) => `${className}${isActive ? " active" : ""}`} />;
});

export default function StaffSidebar({ user, onLogout, onHelp, onLinkClick, collapsed = false, onToggle }) {
  const linkClass = `sidebar-link${collapsed ? " sidebar-link--collapsed" : ""}`;
  const iconClass = `${collapsed ? "w-5 h-5" : "w-4 h-4"} shrink-0`;
  const toggleLabel = collapsed ? "Expand sidebar" : "Collapse sidebar";

  return (
    <>
      <div className={`shrink-0 border-b ${collapsed ? "px-2 py-4 flex flex-col items-center gap-3" : "px-4 py-6 flex items-center gap-2"}`}
           style={{ borderColor: "var(--fb-border)" }}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Logo size={collapsed ? 32 : 40} />
          {!collapsed && <div>
            <div className="font-bold text-base whitespace-nowrap" style={{ fontFamily: "Outfit", color: "var(--fb-text)" }}>
              Pullman Child
            </div>
            <div className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Welfare</div>
          </div>}
        </div>
        {onToggle && <SidebarTip label={toggleLabel} enabled>
          <button type="button" onClick={onToggle} data-testid="desktop-sidebar-toggle"
                  aria-label={toggleLabel} aria-expanded={!collapsed} aria-controls="desktop-sidebar"
                  className="sidebar-toggle shrink-0 p-2 rounded-lg hover:bg-stone-200"
                  style={{ color: "var(--fb-primary)" }}>
            {collapsed ? <PanelLeftOpen className="w-5 h-5" aria-hidden="true" /> :
              <PanelLeftClose className="w-5 h-5" aria-hidden="true" />}
          </button>
        </SidebarTip>}
      </div>
      <div className="flex flex-col flex-1 min-h-0 overflow-y-auto" data-testid="sidebar-scroll-area">
        <nav aria-label="Staff navigation" className={`flex-1 space-y-1 ${collapsed ? "px-2 py-4" : "px-4 py-6"}`}>
          {NAV.filter(n => {
            if (n.adminOnly && user?.role !== "admin") return false;
            if (n.viewerHidden && user?.role === "viewer") return false;
            if (!isPageAllowed(user, n.slug)) return false;
            if (hasClientPrivacy(user) && ["audit", "import"].includes(n.slug)) return false;
            return true;
          }).map(({ to, label, icon: Icon, testid, newTab }) => (
            <SidebarTip key={to} label={label} enabled={collapsed}>
              {newTab ? (
                <a href={to} target="_blank" rel="noopener noreferrer" data-testid={testid}
                   aria-label={label} onClick={onLinkClick} className={linkClass}>
                  <Icon className={iconClass} aria-hidden="true" />
                  {!collapsed && <span className="text-sm">{label}</span>}
                </a>
              ) : (
                <SidebarRouteLink to={to} data-testid={testid} aria-label={label}
                                  onClick={onLinkClick} className={linkClass}>
                  <Icon className={iconClass} aria-hidden="true" />
                  {!collapsed && <span className="text-sm">{label}</span>}
                </SidebarRouteLink>
              )}
            </SidebarTip>
          ))}
        </nav>
        <div className={`shrink-0 border-t py-4 ${collapsed ? "px-2" : "px-4"}`} style={{ borderColor: "var(--fb-border)" }}>
          {!collapsed && <div className="px-3 py-2 mb-2">
            <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>Signed in as</div>
            <div className="text-sm font-medium truncate" data-testid="current-user-email">{user?.name || user?.email}</div>
            {user?.name && <div className="text-xs truncate" style={{ color: "var(--fb-text-muted)" }}>{user.email}</div>}
          </div>}
          <SidebarTip label="My Profile" enabled={collapsed}>
            <SidebarRouteLink to="/staff/profile" data-testid="nav-profile" aria-label="My Profile"
                              onClick={onLinkClick} className={linkClass}>
              <UserCircle className={iconClass} aria-hidden="true" />
              {!collapsed && <span className="text-sm">My Profile</span>}
            </SidebarRouteLink>
          </SidebarTip>
          <SidebarTip label="Sign out" enabled={collapsed}>
            <button type="button" data-testid="logout-btn" aria-label="Sign out" onClick={onLogout}
                    className={`${linkClass} ${collapsed ? "" : "w-full"} mt-1`}>
              <LogOut className={iconClass} aria-hidden="true" />
              {!collapsed && <span className="text-sm">Sign out</span>}
            </button>
          </SidebarTip>
          <SidebarTip label="Help" enabled={collapsed}>
            <button type="button" onClick={onHelp} className={`${linkClass} ${collapsed ? "" : "w-full"} mt-1`} aria-label="Open help">
              <HelpCircle className={iconClass} aria-hidden="true" />
              {!collapsed && <span className="text-sm">Help</span>}
            </button>
          </SidebarTip>
        </div>
      </div>
    </>
  );
}
